import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { notification, Select, Tooltip } from 'antd';
import { Plus, FileText, Share2 } from 'lucide-react';
import { EditOutlined, EyeOutlined, FilePdfOutlined, ShareAltOutlined } from '@ant-design/icons';
import { fetchProject } from '../../redux/actions/projectAction';
import {
  combineCertificates,
  fetchCertificatePdf,
  fetchCertificates,
} from '../../services/arcWelding';
import {
  AW_CERTIFICATE_STATUS,
  AW_FILTER_LABELS,
  isCertificateEditable,
  isCertificateViewOnly,
  normalizePagedResponse,
  openPdfUrl,
  resolvePdfUrl,
} from '../../utils/arcWeldingVerificationUi';
import ArcWeldingCertificateForm from './ArcWeldingCertificateForm';
import ArcWeldingShareModal from './ArcWeldingShareModal';
import ArcWeldingListShell, {
  AwFilterGroup,
  AwFilterSearch,
  AwFilterSelect,
  buildServerPagination,
  buildTextColumn,
  useDebouncedSearch,
} from './ArcWeldingListShell';
import './ArcWelding.css';

const { Option } = Select;
const STATUS_OPTIONS = Object.values(AW_CERTIFICATE_STATUS);

const stopRowClick = (event) => {
  event.stopPropagation();
};

const ArcWeldingCertificatesPage = ({ projects, onFetchProjects, onViewChange }) => {
  const [view, setView] = useState('list');
  const [editingId, setEditingId] = useState(null);
  const [formMode, setFormMode] = useState('edit');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, debouncedSearch, setSearchInput] = useDebouncedSearch();
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [expiringWithinDays, setExpiringWithinDays] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [combining, setCombining] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareTargets, setShareTargets] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});

  useEffect(() => {
    onFetchProjects?.();
  }, [onFetchProjects]);

  useEffect(() => {
    onViewChange?.(view === 'form');
  }, [view, onViewChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchCertificates({
        projectId: projectId ? (Number(projectId) || projectId) : undefined,
        status: status || undefined,
        search: debouncedSearch || undefined,
        expiringWithinDays: expiringWithinDays || undefined,
        pageNumber,
        pageSize,
      });
      const paged = normalizePagedResponse(payload);
      setItems(paged.items);
      setTotal(paged.total);
    } catch (err) {
      notification.error({
        message: 'Failed to load certificates',
        description: err?.message || err?.error || String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, status, debouncedSearch, expiringWithinDays, pageNumber, pageSize]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, projectId, status, expiringWithinDays]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [projectId, status, debouncedSearch, expiringWithinDays]);

  const issuedItems = useMemo(
    () => items.filter((row) => row.status === AW_CERTIFICATE_STATUS.ISSUED),
    [items]
  );

  const selectedIssuedRows = useMemo(
    () => issuedItems.filter((row) => selectedRowKeys.includes(row.id)),
    [issuedItems, selectedRowKeys]
  );

  const toggleRowSelection = useCallback((record) => {
    if (record?.status !== AW_CERTIFICATE_STATUS.ISSUED || !record?.id) return;
    setSelectedRowKeys((prev) =>
      prev.includes(record.id)
        ? prev.filter((key) => key !== record.id)
        : [...prev, record.id]
    );
  }, []);

  const resolveCombineProjectId = useCallback((rows) => {
    const projectIds = [...new Set(rows.map((row) => row.projectId).filter((id) => id != null && id !== ''))];
    if (projectIds.length !== 1) return null;
    const numeric = Number(projectIds[0]);
    return Number.isNaN(numeric) ? projectIds[0] : numeric;
  }, []);

  useEffect(() => {
    if (view === 'list') load();
  }, [view, load]);

  const openCertificate = useCallback((record, mode) => {
    if (!record?.id) return;
    const viewOnly =
      mode === 'view' || (mode !== 'edit' && isCertificateViewOnly(record.status));
    setFormMode(viewOnly ? 'view' : 'edit');
    setEditingId(record.id);
    setView('form');
  }, []);

  const handleCombine = async () => {
    if (selectedIssuedRows.length < 2) {
      notification.warning({
        message: 'Select at least two issued certificates to combine',
      });
      return;
    }
    const combineProjectId = resolveCombineProjectId(selectedIssuedRows);
    if (!combineProjectId) {
      notification.warning({
        message: 'Certificates must belong to the same project',
        description: 'Select issued certificates from one project only.',
      });
      return;
    }
    setCombining(true);
    try {
      const result = await combineCertificates({
        projectId: combineProjectId,
        certificateIds: selectedIssuedRows.map((row) => row.id),
      });
      const url = resolvePdfUrl(result);
      if (url) openPdfUrl(url);
      notification.success({ message: 'Certificates combined' });
      setSelectedRowKeys([]);
    } catch (err) {
      notification.error({
        message: 'Combine failed',
        description: String(err?.message || err?.error || err),
      });
    } finally {
      setCombining(false);
    }
  };

  const handleShareSelected = () => {
    if (!selectedIssuedRows.length) {
      notification.warning({ message: 'Select at least one issued certificate to share' });
      return;
    }
    setShareTarget(null);
    setShareTargets(selectedIssuedRows);
  };

  const handlePdf = async (record, event) => {
    event?.stopPropagation?.();
    if (record?.pdfFileUrl) {
      openPdfUrl(record.pdfFileUrl);
      return;
    }
    try {
      const result = await fetchCertificatePdf(record.id);
      const url = resolvePdfUrl(result);
      if (url) openPdfUrl(url);
    } catch (err) {
      notification.error({ message: 'PDF not available', description: String(err?.message || err) });
    }
  };

  const columns = useMemo(
    () => [
      buildTextColumn('certificateNo', 'Certificate No.', {
        render: (v) => v || 'DRAFT',
        filterValue: columnFilters.certificateNo,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, certificateNo: v })),
      }),
      buildTextColumn('projectName', 'Project', {
        filterValue: columnFilters.projectName,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, projectName: v })),
      }),
      buildTextColumn('siteName', 'Site'),
      buildTextColumn('profileName', 'Profile'),
      {
        title: 'Serial / Asset',
        key: 'serial',
        align: 'left',
        width: 160,
        render: (_, r) => [r.serialNumber, r.assetPlantNo].filter(Boolean).join(' / ') || '—',
      },
      buildTextColumn('testIssueDate', 'Test Date', { width: 120 }),
      buildTextColumn('expiryDate', 'Expiry', { width: 120 }),
      buildTextColumn('status', 'Status', { width: 130 }),
      buildTextColumn('overallResult', 'Result', { width: 110 }),
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 170,
        fixed: 'right',
        render: (_, record) => {
          const editable = isCertificateEditable(record.status);
          const viewOnly = isCertificateViewOnly(record.status);
          const issued = record.status === AW_CERTIFICATE_STATUS.ISSUED;
          return (
            <div className="heat-profile-actions-cell" onClick={stopRowClick}>
              {editable && (
                <Tooltip title="Edit">
                  <EditOutlined
                    className="txtIcon"
                    onClick={() => openCertificate(record, 'edit')}
                  />
                </Tooltip>
              )}
              {viewOnly && (
                <Tooltip title="View">
                  <EyeOutlined
                    className="txtIcon"
                    onClick={() => openCertificate(record, 'view')}
                  />
                </Tooltip>
              )}
              {issued && (
                <>
                  <Tooltip title="Download PDF">
                    <FilePdfOutlined className="txtIcon" onClick={(e) => handlePdf(record, e)} />
                  </Tooltip>
                  <Tooltip title="Share Certificate">
                    <ShareAltOutlined
                      className="txtIcon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareTargets([]);
                        setShareTarget(record);
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [columnFilters, openCertificate]
  );

  const filterBar = (
    <>
      <AwFilterGroup label={AW_FILTER_LABELS.search} className="aw-filter-group--search">
        <AwFilterSearch
          placeholder="Certificate No / Serial"
          value={searchInput}
          onChange={setSearchInput}
        />
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.project} className="aw-filter-group--select-md">
        <AwFilterSelect
          placeholder="All Projects"
          showSearch
          optionFilterProp="children"
          value={projectId}
          onChange={setProjectId}
        >
          {projects.map((p) => (
            <Option key={p.id} value={String(p.id)}>{p.projectName}</Option>
          ))}
        </AwFilterSelect>
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.status} className="aw-filter-group--select">
        <AwFilterSelect
          placeholder="All Statuses"
          value={status}
          onChange={setStatus}
        >
          {STATUS_OPTIONS.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </AwFilterSelect>
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.expiringWithin} className="aw-filter-group--select-sm">
        <AwFilterSelect
          placeholder="Any Time"
          value={expiringWithinDays}
          onChange={setExpiringWithinDays}
        >
          <Option value="30">30 days</Option>
          <Option value="60">60 days</Option>
          <Option value="90">90 days</Option>
        </AwFilterSelect>
      </AwFilterGroup>
    </>
  );

  const clientFiltered = useMemo(() => {
    const entries = Object.entries(columnFilters).filter(([, v]) => v);
    if (!entries.length) return items;
    return items.filter((row) =>
      entries.every(([key, val]) =>
        String(row[key] ?? '').toLowerCase().includes(String(val).toLowerCase())
      )
    );
  }, [items, columnFilters]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
      preserveSelectedRowKeys: true,
      columnWidth: 48,
      getCheckboxProps: (record) => ({
        disabled: record.status !== AW_CERTIFICATE_STATUS.ISSUED,
      }),
    }),
    [selectedRowKeys]
  );

  const combineLabel = selectedIssuedRows.length
    ? `Combine Certificates (${selectedIssuedRows.length})`
    : 'Combine Certificates';

  const shareSelectedLabel = selectedIssuedRows.length
    ? `Share Selected (${selectedIssuedRows.length})`
    : 'Share Selected';

  const canCombine = selectedIssuedRows.length >= 2 && Boolean(resolveCombineProjectId(selectedIssuedRows));

  if (view === 'form') {
    return (
      <ArcWeldingCertificateForm
        certificateId={editingId}
        formMode={formMode}
        projects={projects}
        onBack={() => {
          setView('list');
          setEditingId(null);
          setFormMode('edit');
        }}
        onSaved={(saved) => {
          if (saved?.id) setEditingId(saved.id);
        }}
      />
    );
  }

  return (
    <>
      <ArcWeldingListShell
        title="Arc Welding Certificates"
        description="Arc welding equipment verification certificates per IEC 60974-14."
        count={total}
        headerActions={[
          {
            key: 'share-selected',
            label: shareSelectedLabel,
            variant: 'tertiary',
            icon: Share2,
            disabled: selectedIssuedRows.length === 0,
            onClick: handleShareSelected,
          },
          {
            key: 'combine',
            label: combining ? 'Combining…' : combineLabel,
            variant: 'tertiary',
            icon: FileText,
            disabled: !canCombine || combining,
            onClick: handleCombine,
          },
          {
            key: 'add',
            label: 'Add New',
            variant: 'primary',
            icon: Plus,
            onClick: () => {
              setEditingId(null);
              setFormMode('edit');
              setView('form');
            },
          },
        ]}
        filterBar={filterBar}
        columns={columns}
        dataSource={clientFiltered}
        loading={loading}
        scroll={{ x: 1600 }}
        rowSelection={rowSelection}
        onRow={(record) => ({
          onClick: (event) => {
            if (
              event.target.closest(
                '.heat-profile-actions-cell, .ant-checkbox-wrapper, .ant-checkbox, button, a, input'
              )
            ) {
              return;
            }
            if (record.status === AW_CERTIFICATE_STATUS.ISSUED) {
              toggleRowSelection(record);
              return;
            }
            openCertificate(record);
          },
          className: [
            'aw-certificates-row-clickable',
            selectedRowKeys.includes(record.id) ? 'aw-certificates-row-selected' : '',
          ]
            .filter(Boolean)
            .join(' '),
        })}
        pagination={buildServerPagination({
          total,
          pageNumber,
          pageSize,
          onChange: (page, size) => {
            setPageNumber(page);
            setPageSize(size);
          },
        })}
      />

      <ArcWeldingShareModal
        open={Boolean(shareTarget || shareTargets.length)}
        certificateId={shareTarget?.id}
        certificateNo={shareTarget?.certificateNo}
        certificates={shareTargets}
        onClose={() => {
          setShareTarget(null);
          setShareTargets([]);
        }}
      />
    </>
  );
};

const mapStateToProps = ({ projectRequests }) => ({
  projects: get(projectRequests, 'projectData', []),
});

const mapDispatchToProps = {
  onFetchProjects: fetchProject,
};

export default connect(mapStateToProps, mapDispatchToProps)(ArcWeldingCertificatesPage);
